/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { deviceStats } from '@src/configs/devices.generated'
import {
	Seo,
	Footer,
	KoinosWordmark,
	KedemWordmark,
	LewittFigure,
	LewittHatchFill,
} from '@components/systems'
import { mono, monoShared } from '@components/systems/monolith/monolith.tokens'
import { buildOnlineStoreJsonLd } from '@utils'

import heroImg from '@public/images/monolith/koinos8-top-front-screen-m7.png'
import koinosImg from '@public/images/monolith/koinos8-angle-full.png'
import studioImg from '@public/images/monolith/studio-editor.jpg'

import { useNavigate } from 'react-router-dom'

/**
 * Home — Monolith(2안, 콜드 라이트).
 *
 * 목업: claudeFiles/judahsWeb/homepage-mockups/2안-monolith/index-v2.html
 * 구성: 히어로(워드마크 + 정면 탑뷰 합성컷) → 3 주장(3열, LeWitt 도형) →
 *       카탈로그 3카드 → 시즌 카피 밴드 → 클로즈 → 푸터.
 *
 * PERSONA §3 의 미술 3인이 형식 층위에서 이 페이지를 만든다. 인격(§1)·컬러(§2)는 건드리지 않는다.
 * - LeWitt : 면의 정체를 색이 아니라 선 방향이 정한다. 3 주장의 도형과 사진 없는 카드가 그 자리다.
 * - Judd   : 동일 유닛의 반복이 곧 형식. 세 열이 한 유닛, 세 카드가 한 유닛 — 변주하지 않는다.
 * - 박서보  : 표면은 톤·질감이 아니라 같은 선의 축적. 시즌 밴드 바탕에 그라디언트를 쓰지 않는다.
 *
 * 시즌 카피 "the WAY, the TRUTH, and the LIFE" 는 사이트 전체에서 이 밴드와 푸터 시그니처
 * 두 자리에만 둔다 (HOMEPAGE_MOCKUP_PLAN §2). 다른 곳에 옮겨 쓰지 않는다.
 */
const MonolithHomePage = () => {
	// hook
	const navigate = useNavigate()

	const jsonLd = React.useMemo(() => buildOnlineStoreJsonLd(), [])

	/**
	 * 세 주장. 도형은 주장의 구조를 면 분할로 옮긴 것이다 — 캡션을 붙이지 않는다(PERSONA §4).
	 * 배열(네 방향을 나란히) · 이분(둘 중 하나) · 중첩(조합 전체가 한 자리에).
	 */
	const claimList = [
		{
			id: 'reorder',
			figure: {
				layout: 'bands' as const,
				directions: [
					'vertical' as const,
					'rightDiagonal' as const,
					'horizontal' as const,
					'leftDiagonal' as const,
				],
			},
			title: 'Reorder your pedals with a touch.',
			body: 'No cables. Saved per preset.',
		},
		{
			id: 'impedance',
			figure: {
				layout: 'halves' as const,
				directions: ['vertical' as const, 'horizontal' as const],
			},
			title: 'Choose what your pickups see.',
			body: '500 kΩ or 1 MΩ input, selected on screen.',
		},
		{
			id: 'midi',
			figure: {
				layout: 'stack' as const,
				directions: [
					'vertical' as const,
					'rightDiagonal' as const,
					'horizontal' as const,
					'leftDiagonal' as const,
				],
			},
			title: 'Forget CC numbers.',
			body: `${deviceStats.devices.toLocaleString('en-US')} devices and over ${(
				Math.floor(deviceStats.parameters / 1000) * 1000
			).toLocaleString('en-US')} MIDI parameters, on the unit.`,
		},
	]

	return (
		<View css={style}>
			<Seo jsonLd={jsonLd} />

			{/* hero */}
			<View className="mnWrap mnHero">
				<span className="mnKicker mnHeroKicker">Programmable rig hardware</span>
				<KoinosWordmark as="h1" className="mnHeroWordmark" />
				<p className="mnHeroTagline">Your rig. One screen.</p>
				<View className="mnHeroImageView">
					<img
						src={heroImg}
						alt="Koinos PLS 8, front view with the display on"
						className="mnHeroImage"
					/>
				</View>
				<View className="mnHeroAction">
					<button
						type="button"
						className="mnCta"
						onClick={() => navigate('/products/pls8')}
					>
						View PLS 8
					</button>
				</View>
			</View>

			{/* claims — 3열. 열마다 도형 → 제목 → 한 줄을 가운데 정렬로 쌓는다 */}
			<View className="mnWrap">
				<View className="mnBand mnClaimsBand">
					{claimList.map(aClaim => (
						<View key={aClaim.id} className="mnClaimItem">
							<LewittFigure
								id={`claim-${aClaim.id}`}
								layout={aClaim.figure.layout}
								directions={aClaim.figure.directions}
								size={160}
							/>
							<h3 className="mnClaimTitle">{aClaim.title}</h3>
							<p className="mnClaimBody">{aClaim.body}</p>
						</View>
					))}
				</View>
			</View>

			{/* catalogue — 세 카드가 같은 유닛. 사진이 없는 면은 해칭이 코딩한다 */}
			<View className="mnWrap">
				<View className="mnCatalogueBand">
					<span className="mnKicker mnCatalogueKicker">Catalogue</span>
					<View className="mnCatalogue">
						<a
							className="mnCard"
							href="/products/pls8"
							onClick={e => {
								e.preventDefault()
								navigate('/products/pls8')
							}}
						>
							<img src={koinosImg} alt="Koinos PLS 8" className="mnCardFrame" />
							<View>
								<View className="mnCardTitle">
									<KoinosWordmark as="span" size={28} />
								</View>
								<p className="mnCardBody">
									8 loops, buffered insert, 8.88-inch touch display.
								</p>
							</View>
							<View className="mnCardFoot">
								<span className="mnCardMeta">Not yet open</span>
								<span className="mnTextLink">View product</span>
							</View>
						</a>

						<a
							className="mnCard"
							href="/products/kdm6"
							onClick={e => {
								e.preventDefault()
								navigate('/products/kdm6')
							}}
						>
							<View className="mnCardFrame mnCardHatch" />
							<View>
								<View className="mnCardTitle">
									<KedemWordmark as="span" model="6" size={28} />
								</View>
								<p className="mnCardBody">
									Six switches, 3.5-inch touch display, AirMIDI wireless.
								</p>
							</View>
							<View className="mnCardFoot">
								<span className="mnCardMeta">MIDI control system</span>
								<span className="mnTextLink">View product</span>
							</View>
						</a>

						{/* 세 장이 같은 유닛이므로 이 카드도 통째로 링크다 — 안에 버튼을 두지 않는다.
						    버튼과 span 은 폰트 메트릭이 달라 카드 푸터가 5px 어긋난다 */}
						<a
							className="mnCard"
							href="/software/judahs-studio"
							onClick={e => {
								e.preventDefault()
								navigate('/software/judahs-studio')
							}}
						>
							<img
								src={studioImg}
								alt="Judahs Studio, the companion editor"
								className="mnCardFrame"
							/>
							<View>
								<View className="mnCardTitle">Judahs Studio</View>
								<p className="mnCardBody">
									The companion editor for Koinos hardware. Presets, routing and firmware,
									from a desk.
								</p>
							</View>
							<View className="mnCardFoot">
								<span className="mnCardMeta">macOS · Windows</span>
								<span className="mnTextLink">Learn more</span>
							</View>
						</a>
					</View>
				</View>
			</View>

			{/* season copy band — 바탕은 칠한 면이 아니라 그은 선의 축적 */}
			<View className="mnWrap">
				<View className="mnBand mnSeasonBand">
					<LewittHatchFill id="seasonHatch" className="mnSeasonHatch" />
					<div className="mnSeasonCopyBox">
						{/* 글줄마다 바탕색이 따라붙어 해칭을 끊는다 — 면은 진하게 두고 글자만 판독한다 */}
						<span className="mnSeasonCopy">
							<span className="mnSeasonDim">the</span> WAY
							<span className="mnSeasonDim">, the</span> TRUTH
							<span className="mnSeasonDim">, and the</span> LIFE
						</span>
					</div>
				</View>
			</View>

			{/* close */}
			<View className="mnWrap">
				<View className="mnBand mnClose">
					<h2 className="mnCloseTitle">From today it is your pedalboard.</h2>
					{/* 홈의 마지막 동선 — 제품 페이지로. 거래(`/shop`)가 아니라 무엇인지부터 보여준다 */}
					<View className="mnCloseAction">
						<a
							className="mnCta"
							href="/products/pls8"
							onClick={e => {
								e.preventDefault()
								navigate('/products/pls8')
							}}
						>
							View Koinos PLS 8
						</a>
					</View>
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default MonolithHomePage

/**
 * 네 방향 중 하나로 면을 채우는 CSS 해칭. 각은 그라디언트 축이라 줄무늬는 그 직교 방향으로 선다.
 * 사선을 진하게 쓸 때는 `width` 를 2px 로 올린다 — 1px 사선은 서브픽셀 앤티에일리어싱이
 * 색 번짐(fringing)을 만들어 진해질수록 지저분해진다.
 */
const hatch = (angle: string, stroke: string, gap: number, width = 1) =>
	`repeating-linear-gradient(${angle}, ${stroke} 0 ${width}px, transparent ${width}px ${gap}px)`

const style = css({
	...monoShared,
	position: 'relative',
	width: '100%',

	'.mnHero': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',
		/* padding 축약형을 쓰면 mnWrap 의 좌우 여백까지 0 으로 덮는다 (DESIGN_SYSTEM §10). 세로만 준다 */
		paddingTop: 'clamp(72px, 10vw, 140px)',
		paddingBottom: 'clamp(64px, 8vw, 110px)',

		'.mnHeroKicker': {
			letterSpacing: '.34em',
		},

		'.mnHeroWordmark': {
			marginTop: 44,
			/* 하한 48 — 56 이면 430px 에서 "KOINOS PLS 8" 이 여백을 넘어 가로 스크롤이 생긴다 */
			fontSize: 'clamp(48px, 9.7vw, 140px)',
			color: mono.ink,
		},

		'.mnHeroTagline': {
			marginTop: 40,
			fontSize: 'clamp(20px, 2.1vw, 30px)',
			fontWeight: 200,
			color: mono.dim,
		},

		'.mnHeroImageView': {
			marginTop: 'clamp(48px, 6vw, 90px)',
			width: '100%',
			maxWidth: 1180,

			'.mnHeroImage': {
				width: '100%',
				aspectRatio: '2 / 1',
				objectFit: 'cover',
				display: 'block',
				filter: 'drop-shadow(0 40px 60px rgba(20,24,32,0.16))',
			},
		},

		'.mnHeroAction': {
			marginTop: 'clamp(48px, 5.5vw, 80px)',
		},
	},

	/* 3 주장 — 3열. 열마다 도형 → 제목 → 한 줄. 열 안에서는 전부 가운데 정렬 */
	'.mnClaimsBand': {
		borderTop: `1px solid ${mono.line}`,
		padding: 'clamp(64px, 8vw, 110px) 0',
		display: 'grid',
		gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
		gap: 64,

		'.mnClaimItem': {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			textAlign: 'center',
		},

		'.mnClaimTitle': {
			marginTop: 32,
			fontSize: 26,
			lineHeight: 1.3,
			fontWeight: 400,
			color: mono.ink,
		},
		'.mnClaimBody': {
			marginTop: 16,
			fontSize: 15,
			lineHeight: 1.65,
			fontWeight: 300,
			color: mono.dim,
		},

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 48,
		},
	},

	'.mnCatalogueBand': {
		borderTop: `1px solid ${mono.line}`,
		padding: 'clamp(64px, 8vw, 110px) 0',
	},

	'.mnCatalogueKicker': {
		display: 'block',
		marginBottom: 56,
	},

	'.mnCatalogue': {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
		gap: 40,

		'.mnCard': {
			background: mono.surface,
			border: `1px solid ${mono.line}`,
			padding: 'clamp(24px, 2.8vw, 40px)',
			display: 'flex',
			flexDirection: 'column',
			gap: 28,
			color: mono.ink,

			/* Judd — 세 카드의 이미지 자리가 같은 프레임이다. 보더·radius·필터를 얹지 않는다 */
			'.mnCardFrame': {
				width: '100%',
				aspectRatio: '1.6 / 1',
				objectFit: 'cover',
				display: 'block',
				background: mono.surface,
			},
			/* LeWitt — 사진이 없는 면은 수직선이 코딩한다 */
			'.mnCardHatch': {
				backgroundImage: hatch('90deg', mono.lineInput, 5),
				transition: 'background-image 200ms ease-out',
			},

			'.mnCardTitle': {
				height: 40,
				display: 'flex',
				alignItems: 'center',
				fontSize: 28,
				fontWeight: 400,
				color: mono.ink,
			},
			'.mnCardBody': {
				marginTop: 12,
				fontSize: 15,
				lineHeight: 1.65,
				fontWeight: 300,
				color: mono.dim,
			},

			'.mnCardFoot': {
				marginTop: 'auto',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				gap: 16,

				'.mnCardMeta': {
					fontSize: 12,
					letterSpacing: '.2em',
					textTransform: 'uppercase',
					color: mono.faint,
					fontWeight: 500,
				},
				'.mnTextLink': {
					whiteSpace: 'nowrap',
				},
			},
		},

		/* §4 의 액센트 조항은 웹 2안에서 색이 아니라 농도로 받는다 (DESIGN_SYSTEM §2 — 액센트 0) */
		'a.mnCard:hover .mnCardHatch': {
			backgroundImage: hatch('90deg', mono.dim, 5),
		},

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
		},
	},

	'@media (prefers-reduced-motion: reduce)': {
		'.mnCardHatch': { transition: 'none' },
	},

	'.mnSeasonBand': {
		borderTop: `1px solid ${mono.line}`,
		padding: 'clamp(80px, 10vw, 150px) 0',
		display: 'flex',
		justifyContent: 'center',
		textAlign: 'center',
		/* 박서보 — 그라디언트도 텍스처 이미지도 아니다. 같은 선의 축적이 곧 표면이다 */
		position: 'relative',

		'.mnSeasonHatch': {
			position: 'absolute',
			inset: 0,
			pointerEvents: 'none',
		},

		'.mnSeasonCopyBox': {
			/* 절대배치된 해칭 위로 올라와야 한다 */
			position: 'relative',
			maxWidth: 1100,
			fontSize: 'clamp(30px, 4.4vw, 64px)',
			/* 안쪽 배경판의 좌우 여백만큼 미리 뺀다 — 줄바꿈 계산에 clone 패딩이 안 잡혀
			   좁은 화면에서 첫 줄이 여백을 넘어간다 */
			padding: '0 0.26em',
			boxSizing: 'border-box',
		},

		'.mnSeasonCopy': {
			/* inline + box-decoration-break: clone — 바탕이 글줄마다 따로 붙어 글줄 끝을 감싼다.
			   블록에 배경을 깔면 빈 여백까지 사각형으로 덮여 해칭이 크게 잘려나간다 */
			display: 'inline',
			boxDecorationBreak: 'clone',
			WebkitBoxDecorationBreak: 'clone',
			background: mono.bg,
			padding: '0.06em 0.24em',
			fontSize: 'inherit',
			lineHeight: 1.5,
			fontWeight: 100,
			letterSpacing: '-0.005em',
			color: mono.ink,

			'.mnSeasonDim': {
				color: mono.faint,
			},
		},
	},

	'.mnClose': {
		borderTop: `1px solid ${mono.line}`,
		padding: 'clamp(64px, 8vw, 110px) 0',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',

		'.mnCloseTitle': {
			fontSize: 'clamp(28px, 3vw, 44px)',
			lineHeight: 1.2,
			fontWeight: 300,
			color: mono.ink,
		},
		'.mnCloseAction': {
			marginTop: 44,
		},
	},
})
